import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronsUpDown, Check, Plus, Building2, Loader2, Layers } from "lucide-react";

export function EstablishmentSwitcher() {
  const { orgId, organization, role } = useAuth();
  const qc = useQueryClient();
  const isOwner = role === "owner";
  const canAll = role === "owner" || role === "manager";
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const LKEY = "actipilote.activeLocation";
  const [selectedId, setSelectedId] = useState(() => localStorage.getItem(LKEY));

  const { data } = useQuery({
    queryKey: ["locations", orgId],
    queryFn: async () => (await api.get("/api/locations", { params: { organization_id: orgId } })).data,
    enabled: !!orgId,
  });
  const all = data?.locations || [];
  const active = all.filter((l) => l.active !== false);

  useEffect(() => {
    if (!active.length) return;
    if (selectedId === "all" && canAll) return;
    const valid = active.find((l) => l.id === selectedId);
    if (!valid) {
      const def = active.find((l) => l.is_primary) || active[0];
      setSelectedId(def.id);
      localStorage.setItem(LKEY, def.id);
      qc.invalidateQueries();
    }
  }, [active, selectedId, canAll, qc]);

  const isAll = selectedId === "all" && canAll;
  const selected = isAll ? null : (active.find((l) => l.id === selectedId) || active[0]);

  const select = (id) => {
    setSelectedId(id);
    localStorage.setItem(LKEY, id);
    setOpen(false);
    qc.invalidateQueries();
    toast.success(id === "all" ? "Tous les établissements" : `Établissement : ${active.find((x) => x.id === id)?.name}`);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            data-testid="establishment-switcher"
            className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-white/10"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#10B981]/15 font-semibold text-[#10B981]">
              {isAll ? <Layers className="h-4 w-4" /> : (selected?.name || organization?.name || "?").charAt(0)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-white">{isAll ? "Tous les établissements" : (selected?.name || organization?.name || "—")}</p>
              <p className="truncate text-xs text-slate-400">{isAll ? "Vue agrégée" : (selected?.city || organization?.city)}</p>
            </div>
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-500" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" side="top" className="w-64 p-1.5" data-testid="establishment-list">
          <p className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">Établissements</p>
          <div className="max-h-64 overflow-y-auto">
            {canAll && (
              <button onClick={() => select("all")} data-testid="establishment-option-all"
                className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm hover:bg-slate-100">
                <Layers className="h-4 w-4 shrink-0 text-slate-400" />
                <div className="min-w-0 flex-1 font-medium text-slate-800">Tous les établissements</div>
                {isAll && <Check className="h-4 w-4 shrink-0 text-primary" />}
              </button>
            )}
            {active.map((l) => (
              <button
                key={l.id}
                onClick={() => select(l.id)}
                data-testid={`establishment-option-${l.id}`}
                className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm hover:bg-slate-100"
              >
                <Building2 className="h-4 w-4 shrink-0 text-slate-400" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-slate-800">{l.name}</p>
                  {l.city && <p className="truncate text-xs text-slate-400">{l.city}</p>}
                </div>
                {l.id === selected?.id && <Check className="h-4 w-4 shrink-0 text-primary" data-testid="establishment-current" />}
              </button>
            ))}
          </div>
          {isOwner && (
            <>
              <div className="my-1 h-px bg-slate-100" />
              <button
                onClick={() => { setOpen(false); setCreateOpen(true); }}
                data-testid="add-establishment-button"
                className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-primary hover:bg-blue-50"
              >
                <Plus className="h-4 w-4" /> Ajouter un établissement
              </button>
            </>
          )}
        </PopoverContent>
      </Popover>

      <EstablishmentDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(loc) => { qc.invalidateQueries({ queryKey: ["locations", orgId] }); select(loc.id); }}
      />
    </>
  );
}

export function EstablishmentDialog({ open, onClose, onCreated, editing }) {
  const { orgId } = useAuth();
  const [form, setForm] = useState({ name: "", city: "", country: "" });

  useEffect(() => {
    if (open) setForm(editing ? { name: editing.name || "", city: editing.city || "", country: editing.country || "" } : { name: "", city: "", country: "" });
  }, [open, editing]);

  const mut = useMutation({
    mutationFn: async () => {
      if (editing) return (await api.patch(`/api/locations/${editing.id}`, form, { params: { organization_id: orgId } })).data;
      return (await api.post("/api/locations", form, { params: { organization_id: orgId } })).data;
    },
    onSuccess: (loc) => { toast.success(editing ? "Établissement mis à jour" : "Établissement créé"); onCreated?.(loc); onClose(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="establishment-dialog">
        <DialogHeader><DialogTitle>{editing ? "Modifier l'établissement" : "Nouvel établissement"}</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); if (form.name.trim()) mut.mutate(); }} className="space-y-4">
          <Field label="Nom *" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} testid="establishment-name" autoFocus />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ville" value={form.city} onChange={(v) => setForm((f) => ({ ...f, city: v }))} testid="establishment-city" />
            <Field label="Pays" value={form.country} onChange={(v) => setForm((f) => ({ ...f, country: v }))} testid="establishment-country" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Annuler</button>
            <button type="submit" disabled={mut.isPending || !form.name.trim()} data-testid="establishment-save" className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
              {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Enregistrer" : "Créer"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, value, onChange, testid, autoFocus }) => (
  <div>
    <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
    <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} autoFocus={autoFocus}
      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
  </div>
);
